/* Gelo · Engine — animates the engineering render in place: water flow along the real pipes,
   light play on the water and a per-callout spotlight. The photo itself is never redrawn.
   Coordinates are tuned for the current engine render, in a 1100 x 738 reference space. */
(function () {
  'use strict';

  var IMG_W = 1100, IMG_H = 738;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hoverCapable = window.matchMedia('(hover: hover)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  var threeLoading = null;
  function loadThree(src) {
    if (window.THREE) return Promise.resolve();
    if (threeLoading) return threeLoading;
    threeLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return threeLoading;
  }

  function mount(media) {
    if (media.dataset.geloEngineMounted) return;
    var canvas = media.querySelector('[data-gelo-engine-canvas]');
    if (!canvas || reducedMotion.matches || saveData || !hasWebGL()) return;
    media.dataset.geloEngineMounted = '1';
    var grid = media.closest('.gelo-engine__grid');
    var callouts = grid ? Array.prototype.slice.call(grid.querySelectorAll('.gelo-engine__callout')) : [];
    var near = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        near.disconnect();
        loadThree(canvas.dataset.threeSrc)
          .then(function () { start(media, canvas, callouts); })
          .catch(function () { /* the static image stays */ });
      });
    }, { rootMargin: '400px 0px' });
    near.observe(media);
  }

  function start(media, canvas, callouts) {
    var THREE = window.THREE;
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    var scene = new THREE.Scene();
    var camera = new THREE.OrthographicCamera(0, IMG_W, IMG_H, 0, -50, 50);

    function wy(y) { return IMG_H - y; }

    // ---------- mask painting ----------
    function softPoly(ctx, pts, color, feather) {
      var cx = 0, cy = 0;
      pts.forEach(function (p) { cx += p[0]; cy += p[1]; });
      cx /= pts.length; cy /= pts.length;
      ctx.globalCompositeOperation = 'lighter';
      for (var i = feather; i >= 0; i--) {
        var s = 1 + i * 0.02;
        ctx.globalAlpha = i === 0 ? 0.55 : 0.11;
        ctx.fillStyle = color;
        ctx.beginPath();
        pts.forEach(function (p, j) {
          var x = cx + (p[0] - cx) * s, y = cy + (p[1] - cy) * s;
          if (j === 0) ctx.moveTo(x / 2, y / 2); else ctx.lineTo(x / 2, y / 2);
        });
        ctx.closePath();
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    function softEllipse(ctx, x, y, rx, ry, color, inner) {
      ctx.globalCompositeOperation = 'lighter';
      var g = ctx.createRadialGradient(x / 2, y / 2, 0, x / 2, y / 2, Math.max(rx, ry) / 2);
      var mid = inner == null ? 0.7 : inner;
      g.addColorStop(0, color);
      g.addColorStop(mid, color);
      g.addColorStop(mid + (1 - mid) * 0.5, color.replace('rgb(', 'rgba(').replace(')', ',0.35)'));
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.save();
      ctx.translate(x / 2, y / 2);
      ctx.scale(1, ry / rx);
      ctx.translate(-x / 2, -y / 2);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x / 2, y / 2, rx / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    function softRect(ctx, x, y, w, h, r, color) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo((x + r) / 2, y / 2);
      ctx.arcTo((x + w) / 2, y / 2, (x + w) / 2, (y + h) / 2, r / 2);
      ctx.arcTo((x + w) / 2, (y + h) / 2, x / 2, (y + h) / 2, r / 2);
      ctx.arcTo(x / 2, (y + h) / 2, x / 2, y / 2, r / 2);
      ctx.arcTo(x / 2, y / 2, (x + w) / 2, y / 2, r / 2);
      ctx.closePath();
      ctx.fill();
    }
    function fillPoly(ctx, pts, color) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = color;
      ctx.beginPath();
      pts.forEach(function (p, j) {
        if (j === 0) ctx.moveTo(p[0] / 2, p[1] / 2); else ctx.lineTo(p[0] / 2, p[1] / 2);
      });
      ctx.closePath();
      ctx.fill();
    }
    // separable box blur, two passes (close to gaussian), in place
    function boxBlur(src, w, h, r) {
      var tmp = new Float32Array(w * h);
      var n = 2 * r + 1;
      for (var pass = 0; pass < 2; pass++) {
        for (var y = 0; y < h; y++) {
          var row = y * w, sum = 0;
          for (var k = -r; k <= r; k++) sum += src[row + Math.min(w - 1, Math.max(0, k))];
          for (var x = 0; x < w; x++) {
            tmp[row + x] = sum / n;
            sum += src[row + Math.min(w - 1, x + r + 1)] - src[row + Math.max(0, x - r)];
          }
        }
        for (var x2 = 0; x2 < w; x2++) {
          var s2 = 0;
          for (var k2 = -r; k2 <= r; k2++) s2 += tmp[Math.min(h - 1, Math.max(0, k2)) * w + x2];
          for (var y2 = 0; y2 < h; y2++) {
            src[y2 * w + x2] = s2 / n;
            s2 += tmp[Math.min(h - 1, y2 + r + 1) * w + x2] - tmp[Math.max(0, y2 - r) * w + x2];
          }
        }
      }
    }

    // effects mask: R = light play inside the water block, G = sheen on the surface
    var fxC = document.createElement('canvas');
    fxC.width = 550; fxC.height = 369;
    var fxCtx = fxC.getContext('2d');
    softPoly(fxCtx, [[288, 224], [436, 205], [439, 426], [365, 433], [363, 550], [305, 564]], 'rgb(255,0,0)', 4);
    softEllipse(fxCtx, 356, 182, 100, 34, 'rgb(0,180,0)');
    var maskFx = new THREE.CanvasTexture(fxC);

    // spotlight mask: R = cooling, G = treatment, B = thermal foam (classified from the photo itself)
    var MASK_W = 550, MASK_H = 369;
    var spC = document.createElement('canvas');
    spC.width = MASK_W; spC.height = MASK_H;
    var spCtx = spC.getContext('2d');
    var maskSpot = new THREE.CanvasTexture(spC);
    var hitMasks = null; // the same three masks, kept for pointer hit-testing over the photo

    function buildSpotMask(photo) {
      var W = MASK_W, H = MASK_H, N = W * H;
      // condenser block, compressor and base plate as one outline, notch above the compressor
      fillPoly(spCtx, [[672, 358], [850, 298], [854, 380], [970, 388], [972, 552], [874, 606], [672, 568]], 'rgb(255,0,0)');
      softRect(spCtx, 600, 255, 100, 285, 36, 'rgb(0,255,0)');   // cartridge + filter housing
      softRect(spCtx, 720, 226, 85, 86, 20, 'rgb(0,255,0)');     // ozone unit
      var img = spCtx.getImageData(0, 0, W, H);
      var d = img.data;
      var pc = document.createElement('canvas');
      pc.width = W; pc.height = H;
      var pctx = pc.getContext('2d');
      pctx.drawImage(photo, 0, 0, W, H);
      var p;
      try {
        p = pctx.getImageData(0, 0, W, H).data;
      } catch (e) {
        p = null; // cross-origin image: keep the machine masks, skip the foam
      }
      var R = new Float32Array(N), G = new Float32Array(N), B = new Float32Array(N);
      for (var i = 0; i < N; i++) {
        R[i] = d[i * 4] / 255;
        G[i] = d[i * 4 + 1] / 255;
        // gold foam: warm hue between the orange handles and yellow-white, saturated, inside the tub area
        if (p && i % W < 270) {
          var r = p[i * 4], g = p[i * 4 + 1], b = p[i * 4 + 2];
          var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
          if (mx === r && r > 110 && (mx - mn) / mx > 0.3) {
            var hue = 60 * (g - b) / (mx - mn);
            if (hue > 32 && hue < 62) B[i] = 1;
          }
        }
      }
      boxBlur(B, W, H, 8);
      for (i = 0; i < N; i++) B[i] = B[i] > 0.08 ? 1 : 0;
      boxBlur(B, W, H, 8);
      boxBlur(R, W, H, 8);
      boxBlur(G, W, H, 8);
      for (i = 0; i < N; i++) {
        d[i * 4] = R[i] * 255; d[i * 4 + 1] = G[i] * 255; d[i * 4 + 2] = B[i] * 255; d[i * 4 + 3] = 255;
      }
      spCtx.putImageData(img, 0, 0);
      maskSpot.needsUpdate = true;
      hitMasks = [R, G, B];
    }

    // ---------- photo plane ----------
    var uniforms = {
      map: { value: null },
      maskFx: { value: maskFx },
      maskSpot: { value: maskSpot },
      uTime: { value: 0 },
      uSpotAmt: { value: 0 },
      uSpotSel: { value: new THREE.Vector3(0, 0, 0) }
    };
    var photoMat = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: [
        'varying vec2 vUv;',
        'void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }'
      ].join('\n'),
      fragmentShader: [
        'uniform sampler2D map; uniform sampler2D maskFx; uniform sampler2D maskSpot;',
        'uniform float uTime; uniform float uSpotAmt; uniform vec3 uSpotSel;',
        'varying vec2 vUv;',
        'void main(){',
        '  vec4 fx = texture2D(maskFx, vUv);',
        '  vec3 c = texture2D(map, vUv).rgb;',
        // light play only: pixels never move, so every edge stays exactly where the photo has it
        '  float caus = 0.5 + 0.5*sin(vUv.x*38.0 + sin(vUv.y*21.0 + uTime*0.7)*1.5 + uTime*0.9);',
        '  caus *= 0.5 + 0.5*sin(vUv.y*27.0 - uTime*0.6 + sin(vUv.x*17.0)*1.2);',
        '  c += fx.r * caus * vec3(0.04, 0.055, 0.065);',
        '  c += fx.g * 0.045 * (0.5 + 0.5*sin(vUv.x*34.0 - uTime*0.8));',
        '  float sel = smoothstep(0.0, 1.0, clamp(dot(texture2D(maskSpot, vUv).rgb, uSpotSel), 0.0, 1.0));',
        '  float dim = mix(1.0, mix(0.80, 1.0, sel), uSpotAmt);',
        '  vec3 grey = vec3(dot(c, vec3(0.299, 0.587, 0.114)));',
        '  c = mix(c, mix(grey * 0.95, c, sel), uSpotAmt * 0.35);',
        '  gl_FragColor = vec4(c * dim, 1.0);',
        '}'
      ].join('\n')
    });
    var plane = new THREE.Mesh(new THREE.PlaneGeometry(IMG_W, IMG_H), photoMat);
    plane.position.set(IMG_W / 2, IMG_H / 2, 0);
    scene.add(plane);

    var ready = false;
    var loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(canvas.dataset.src, function (tex) {
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
      uniforms.map.value = tex;
      buildSpotMask(tex.image);
      ready = true;
      wake();
    });

    // ---------- sprite textures ----------
    function dotTexture(inner, outer) {
      var c = document.createElement('canvas');
      c.width = 64; c.height = 64;
      var ctx = c.getContext('2d');
      var g = ctx.createRadialGradient(32, 32, 0, 32, 32, 30);
      g.addColorStop(0, inner);
      g.addColorStop(0.35, outer);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    }
    var dotTex = dotTexture('rgba(255,255,255,0.95)', 'rgba(72,190,255,0.75)');
    var ringC = document.createElement('canvas');
    ringC.width = 256; ringC.height = 256;
    var rctx = ringC.getContext('2d');
    rctx.strokeStyle = 'rgba(255,255,255,0.9)';
    rctx.lineWidth = 5;
    rctx.shadowColor = 'rgba(255,255,255,0.9)';
    rctx.shadowBlur = 10;
    rctx.beginPath();
    rctx.arc(128, 128, 110, 0, Math.PI * 2);
    rctx.stroke();
    var ringTex = new THREE.CanvasTexture(ringC);

    function sprite(tex, color) {
      var m = new THREE.SpriteMaterial({
        map: tex, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthTest: false
      });
      if (color) m.color.set(color);
      var s = new THREE.Sprite(m);
      s.position.z = 1;
      scene.add(s);
      return s;
    }

    // ---------- flow path traced from the render ----------
    var FLOW_IMG = [
      [415, 517], [455, 525], [500, 535], [545, 548], [575, 557], [585, 566], [588, 578],
      [578, 590], [598, 598], [628, 596], [642, 577], [652, 556], [654, 532], [652, 514],
      [650, 486], [650, 458], [650, 443], [668, 442], [684, 441], [694, 432], [695, 406],
      [697, 383], [718, 379], [748, 387], [768, 394],
      [800, 430], [802, 468],
      [622, 443], [596, 430], [578, 413], [572, 394], [571, 362], [566, 343], [548, 334],
      [528, 330], [500, 326], [468, 320]
    ];
    var flowCurve = new THREE.CatmullRomCurve3(
      FLOW_IMG.map(function (p) { return new THREE.Vector3(p[0], wy(p[1]), 0.5); }),
      false, 'catmullrom', 0.25
    );
    // u-range hidden inside the chiller block (between coil entry and cap-left re-emergence)
    var uHideA = 0, uHideB = 0;
    (function () {
      var inP = new THREE.Vector3(768, wy(394), 0.5);
      var outP = new THREE.Vector3(622, wy(443), 0.5);
      var bestA = 1e9, bestB = 1e9;
      for (var i = 0; i <= 800; i++) {
        var u = i / 800;
        var p = flowCurve.getPointAt(u);
        var dA = p.distanceTo(inP), dB = p.distanceTo(outP);
        if (dA < bestA) { bestA = dA; uHideA = u; }
        if (dB < bestB) { bestB = dB; uHideB = u; }
      }
    })();
    function smoothstep(a, b, x) {
      var u = Math.min(1, Math.max(0, (x - a) / (b - a)));
      return u * u * (3 - 2 * u);
    }
    var NPART = 22;
    var parts = [];
    for (var i = 0; i < NPART; i++) {
      var s = sprite(dotTex);
      s.scale.set(15, 15, 1);
      parts.push(s);
    }

    var RIPPLE = { x: 345, y: 175 };
    var rings = [sprite(ringTex), sprite(ringTex)];

    var led = sprite(dotTex, 0x9fdcff);
    led.position.set(747, wy(231), 1);
    led.scale.set(26, 26, 1);

    // ---------- highlight: hover previews a system; on touch, tap selects and tap again unselects ----------
    var textHover = 0, imageHover = 0, pinned = 0;
    var spotGoal = 0;
    var systems = callouts.slice(0, 3);

    function currentView() { return textHover || imageHover || pinned; }
    function updateSpot() {
      var k = currentView();
      uniforms.uSpotSel.value.set(k === 1 ? 1 : 0, k === 2 ? 1 : 0, k === 3 ? 1 : 0);
      spotGoal = k ? 1 : 0;
      systems.forEach(function (el, idx) {
        var n = idx + 1;
        el.classList.toggle('is-hot', n === k);
        el.classList.toggle('is-active', n === pinned);
        el.setAttribute('aria-pressed', String(n === pinned));
      });
      wake();
    }
    // unselecting releases the highlight at once: a tapped item still counts as hovered and focused
    function togglePin(k) {
      if (pinned === k) {
        pinned = 0;
        textHover = 0;
        imageHover = 0;
      } else {
        pinned = k;
      }
      updateSpot();
    }
    systems.forEach(function (el, idx) {
      var k = idx + 1;
      el.classList.add('is-interactive');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-pressed', 'false');
      el.addEventListener('mouseenter', function () { textHover = k; updateSpot(); });
      el.addEventListener('mouseleave', function () { textHover = 0; updateSpot(); });
      el.addEventListener('focus', function () { textHover = k; updateSpot(); });
      el.addEventListener('blur', function () { textHover = 0; updateSpot(); });
      // with a mouse, hover already shows the system, so a click has nothing to do; touch and keyboard toggle
      el.addEventListener('click', function () { if (!hoverCapable) togglePin(k); });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePin(k); }
      });
    });

    // the part under the pointer, read from the same masks the shader uses
    function systemAt(clientX, clientY) {
      if (!hitMasks) return 0;
      var rect = media.getBoundingClientRect();
      var x = Math.floor((clientX - rect.left) / rect.width * MASK_W);
      var y = Math.floor((clientY - rect.top) / rect.height * MASK_H);
      if (x < 0 || y < 0 || x >= MASK_W || y >= MASK_H) return 0;
      var i = y * MASK_W + x, best = 0, bestValue = 0.45;
      for (var k = 0; k < hitMasks.length; k++) {
        if (hitMasks[k][i] > bestValue) { bestValue = hitMasks[k][i]; best = k + 1; }
      }
      return best;
    }
    if (hoverCapable) {
      media.addEventListener('pointermove', function (e) {
        var k = systemAt(e.clientX, e.clientY);
        if (k === imageHover) return;
        imageHover = k;
        media.style.cursor = k ? 'pointer' : '';
        updateSpot();
      });
      media.addEventListener('pointerleave', function () {
        if (!imageHover) return;
        imageHover = 0;
        media.style.cursor = '';
        updateSpot();
      });
    } else {
      media.addEventListener('click', function (e) {
        var k = systemAt(e.clientX, e.clientY);
        if (k) togglePin(k); else if (pinned) togglePin(pinned);
      });
    } 

    // ---------- sizing ----------
    function fit() {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(media.clientWidth, media.clientHeight, false);
      wake();
    }
    new ResizeObserver(fit).observe(media);
    fit();

    // ---------- loop: runs only while the section is on screen ----------
    var visible = false, running = false;
    var prev = 0, clockT = 0;
    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) wake();
    }, { threshold: 0 }).observe(media);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) wake(); });

    function wake() {
      if (running || !ready) return;
      running = true;
      prev = performance.now();
      requestAnimationFrame(animate);
    }

    function animate(now) {
      if (!visible || document.hidden) { running = false; return; }
      requestAnimationFrame(animate);
      var dt = Math.min(0.05, (now - prev) / 1000);
      prev = now;
      clockT += dt;
      var t = clockT;
      uniforms.uTime.value = t;
      uniforms.uSpotAmt.value += (spotGoal - uniforms.uSpotAmt.value) * (1 - Math.exp(-dt * 7));

      var u0 = (t * 0.055) % 1;
      for (var i = 0; i < NPART; i++) {
        var u = (u0 + i / NPART) % 1;
        var p = flowCurve.getPointAt(u);
        parts[i].position.set(p.x, p.y, 1);
        var a = smoothstep(0.0, 0.025, u) * (1 - smoothstep(0.975, 1.0, u));
        if (u > uHideA && u < uHideB) a = 0;
        parts[i].material.opacity = a * 0.85;
        var sc = 13 + 3 * Math.sin(u * 40 + t * 3);
        parts[i].scale.set(sc, sc, 1);
      }
      rings.forEach(function (ring, idx) {
        var ph = ((t * 0.16) + idx * 0.5) % 1;
        var s = 30 + ph * 150;
        ring.position.set(RIPPLE.x, wy(RIPPLE.y), 1);
        ring.scale.set(s, s * 0.32, 1);
        ring.material.opacity = Math.sin(ph * Math.PI) * (1 - ph) * 0.35;
      });
      led.material.opacity = 0.35 + 0.3 * (0.5 + 0.5 * Math.sin(t * 2.2));
      var ls = 24 + 5 * Math.sin(t * 2.2);
      led.scale.set(ls, ls, 1);

      renderer.render(scene, camera);
      if (!canvas.classList.contains('is-ready')) canvas.classList.add('is-ready');
    }
  }

  function mountAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-gelo-engine-media]'), mount);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mountAll(document); });
  } else {
    mountAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
})();
